import type {
  BackendProductUpsertOperationNameEnum,
  ObpSection,
  SelectedProduct,
} from "@credit-acceptance/step6-bff-client";
import type { TFunction } from "i18next";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SubsectionHeader } from "../../../../components/ui/subsectionHeader";
import { formatCurrency } from "../../../../lib/money";
import { getErrorMessage } from "../../../../lib/query/errors";
import Section from "../../components/Section";
import { unmappedMessages } from "../../serverViolations";
import { useApplyServerFieldErrors } from "../../useApplyServerFieldErrors";
import { useRegisterSaveableSection } from "../../useRegisterSaveableSection";
import { ProductDisplayNameMap } from "./forms/constants";
import { StandardProductFields } from "./forms/StandardProductFields";
import { findContractObpProduct, type ObpFormValues, toObpFormValues, toObpSaveRequest } from "./Obp.mapping";
import { formatObpUnmapped, translateObpViolations } from "./Obp.violations";
import { ObpProductOriginalsModal, type OriginalRow } from "./ObpProductOriginalsModal";

interface ObpProperties {
  /** Hide controls and unregister saving without discarding the draft. */
  readonly visible?: boolean;
  readonly obp?: ObpSection;
  /** `additionalProducts.contractObp` — the dealer's Contract/RISC values for the click-through popup. */
  readonly contractObp?: SelectedProduct[];
  /** True when the Contract Worksheet Service read failed, so `contractObp` is absent because the read failed, not because there's no data. */
  readonly contractValuesUnavailable?: boolean;
  /** Submit checklist verdict for OBP (STP6OTBDPS). */
  readonly hasChecklistWarning?: boolean;
  readonly editable?: boolean;
  /** Locks inputs through the save and dashboard refresh without excluding the save payload. */
  readonly isSaving?: boolean;
}

function toOriginalRows(t: TFunction, product?: SelectedProduct): OriginalRow[] {
  return [
    { label: t("obp.productOriginals.vendor"), value: product?.vendorName },
    {
      label: t("obp.productOriginals.sellingPrice"),
      value: product?.sellingPrice == null ? undefined : formatCurrency(product.sellingPrice),
    },
    { label: t("obp.productOriginals.productType"), value: product?.productType },
  ];
}

function Obp({
  obp,
  contractObp,
  contractValuesUnavailable,
  hasChecklistWarning = false,
  editable = false,
  isSaving = false,
  visible = true,
}: ObpProperties) {
  const { t } = useTranslation();
  const form = useForm<ObpFormValues>({ defaultValues: toObpFormValues(obp) });
  const { reset, getValues } = form;
  const { unmapped, apply: applyServerErrors } = useApplyServerFieldErrors(form);
  const [saveError, setSaveError] = useState<string>();
  const [originalsOpen, setOriginalsOpen] = useState(false);
  const [originalsProduct, setOriginalsProduct] = useState<BackendProductUpsertOperationNameEnum>();
  const originalsTrigger = useRef<HTMLButtonElement>(null);

  function openOriginals(name: BackendProductUpsertOperationNameEnum) {
    return (event: MouseEvent<HTMLButtonElement>) => {
      originalsTrigger.current = event.currentTarget;
      setOriginalsProduct(name);
      setOriginalsOpen(true);
    };
  }
  const [lookupErrors, setLookupErrors] = useState<Record<number, string>>({});
  const handleLookupError = useCallback((index: number, message?: string) => {
    setLookupErrors((previous) => {
      if (previous[index] === message) {
        return previous;
      }
      const { [index]: _removed, ...rest } = previous;
      return message ? { ...rest, [index]: message } : rest;
    });
  }, []);
  const applyValidations = useCallback(
    (validations: ObpSection["validations"]) =>
      applyServerErrors(translateObpViolations(validations, getValues().products)),
    [applyServerErrors, getValues]
  );

  const snapshotKey = JSON.stringify(obp);
  useEffect(() => {
    reset(toObpFormValues(obp));
    applyValidations(obp?.validations);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identical refetches must not discard unsaved edits
  }, [snapshotKey, reset, applyValidations]);

  useRegisterSaveableSection(
    "obp",
    {
      // Like Driver, validations are authoritative BFF feedback, never a save gate.
      validate: async () => true,
      getPayload: () =>
        visible && editable && obp?.products?.length
          ? { additionalProducts: { obp: toObpSaveRequest(getValues()) } }
          : {},
      onSaved: (outcome) => {
        setSaveError(undefined);
        const savedObp = outcome.additionalProducts?.obp;
        // The save response is the source of truth, rejected and absent products included.
        if (outcome.refreshFailed) {
          reset(undefined, { keepValues: true, keepErrors: true });
        } else {
          reset(toObpFormValues(savedObp));
        }
        applyValidations(savedObp?.validations);
      },
      onSaveFailed: (error) => setSaveError(getErrorMessage(error)),
    },
    visible
  );

  if (!visible) {
    return null;
  }

  const originalsProductDisplayName = originalsProduct
    ? (ProductDisplayNameMap[originalsProduct] ?? originalsProduct)
    : "";
  const originalsMatch = originalsProduct ? findContractObpProduct(contractObp, originalsProduct) : undefined;

  return (
    <Section title={t("obp.sectionTitle")} scrollSpyCategory="Other Backend Products" hasError={hasChecklistWarning}>
      {unmappedMessages(unmapped, formatObpUnmapped).map((message, index) => (
        <p key={`${index}-${message}`} role="alert" className="text-destructive text-2xs">
          {message}
        </p>
      ))}
      {[...new Set(Object.values(lookupErrors))].map((message) => (
        <p key={message} role="alert" className="text-destructive text-2xs">
          {message}
        </p>
      ))}
      {saveError && (
        <p role="alert" className="text-destructive text-2xs">
          {saveError}
        </p>
      )}
      <div className="@container">
        <div className="@xl:grid-cols-2 @4xl:grid-cols-3 grid grid-cols-1 gap-x-6 gap-y-4">
          {(obp?.products ?? []).map((product, index) => (
            <div key={product.name} className="flex min-w-0 flex-col gap-2">
              <SubsectionHeader>
                <button type="button" className="underline underline-offset-2" onClick={openOriginals(product.name)}>
                  {ProductDisplayNameMap[product.name] ?? product.name}
                </button>
              </SubsectionHeader>
              <StandardProductFields
                form={form}
                index={index}
                productName={product.name}
                readOnly={!editable || isSaving}
                onLookupError={handleLookupError}
              />
            </div>
          ))}
        </div>
      </div>
      <ObpProductOriginalsModal
        open={originalsOpen}
        onOpenChange={setOriginalsOpen}
        triggerRef={originalsTrigger}
        title={t("obp.productOriginals.title", { product: originalsProductDisplayName })}
        rows={toOriginalRows(t, originalsMatch)}
        unavailable={contractValuesUnavailable}
      />
    </Section>
  );
}

export default Obp;
